import React from "react";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

/* ===================== Toast popup ===================== */
function Toast({ open, type = "success", message = "", autoClose = false, onClose = () => {} }) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      const t = setTimeout(() => setVisible(true), 10);
      let fadeTimer, closeTimer;
      if (autoClose) {
        fadeTimer = setTimeout(() => setVisible(false), 2300);
        closeTimer = setTimeout(onClose, 2600);
      }
      return () => {
        clearTimeout(t);
        if (fadeTimer) clearTimeout(fadeTimer);
        if (closeTimer) clearTimeout(closeTimer);
      };
    } else {
      setVisible(false);
    }
  }, [open, autoClose, onClose]);

  if (!open) return null;

  const isSuccess = type === "success";
  const baseColor = isSuccess ? "bg-green-600" : "bg-red-600";
  const borderColor = isSuccess ? "border-green-700" : "border-red-700";

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pointer-events-none">
      <div
        className={`mt-4 w-[min(95%,620px)] pointer-events-auto rounded-lg shadow-xl border ${borderColor} ${baseColor} text-white transition-all duration-300
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}
        role="alert"
      >
        <div className="px-4 py-3 flex items-start gap-3">
          <div className="flex-1">
            <div className="font-semibold">
              {isSuccess ? "Configuración guardada" : "Error al guardar"}
            </div>
            <div className="text-sm opacity-90">{message}</div>
          </div>
          {!autoClose && (
            <button
              type="button"
              onClick={onClose}
              className="ml-2 rounded px-2 py-1 text-xs bg-white/10 hover:bg-white/20"
              title="Cerrar"
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
/* ======================================================= */

/* ----------------- UI helpers ----------------- */
function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="mb-4 rounded-md border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="font-semibold text-gray-800">{title}</span>
        <FaChevronDown
          className={`transition-transform text-gray-500 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function Switch({ checked, onChange, label, disabled = false }) {
  return (
    <label
      className={`inline-flex items-center gap-2 select-none ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <span
        className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${
          checked ? "bg-green-600" : "bg-gray-300"
        }`}
        onClick={() => !disabled && onChange(!checked)}
        aria-disabled={disabled}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </span>
      {label ? <span className="value">{label}</span> : null}
    </label>
  );
}

function Pill({ children, onRemove }) {
  return (
    <span className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-sm bg-gray-100 border border-gray-200">
      {children}
      {onRemove && (
        <button
          type="button"
          className="text-gray-500 hover:text-gray-700"
          onClick={onRemove}
          title="Quitar"
        >
          ×
        </button>
      )}
    </span>
  );
}

/* ----------------- Mapeos / helpers de datos ----------------- */
const TIPO_DIA = "DIA";
const TIPO_LIM_CRED_DIA = "LIMITE_CREDITO_DIARIO";
const TIPO_LIM_CRED_SEM = "LIMITE_CREDITO_SEMANAL";
const TIPO_CANT_CARGAS_DIA = "CANTIDAD_CARGAS_DIARIA";
const TIPO_CARGA_MANUAL = "CARGA_MANUAL"; // lectura legacy
const TIPO_CARGA_MANUAL_NOTIFICAR = "CARGA_MANUAL_NOTIFICAR"; // escritura actual

const normalize = (s) => (s || "").toString().trim().toUpperCase();
const yes = (s) => normalize(s) === "S" || normalize(s) === "SI";

const toKeyDia = (valor) => {
  const v = normalize(valor)
    .replace("Á", "A")
    .replace("É", "E")
    .replace("Í", "I")
    .replace("Ó", "O")
    .replace("Ú", "U");
  if (v === "LUNES") return "lun";
  if (v === "MARTES") return "mar";
  if (v === "MIERCOLES") return "mie";
  if (v === "JUEVES") return "jue";
  if (v === "VIERNES") return "vie";
  if (v === "SABADO") return "sab";
  if (v === "DOMINGO") return "dom";
  return null;
};

const DIAS_LABELS = {
  lun: "LUNES",
  mar: "MARTES",
  mie: "MIERCOLES",
  jue: "JUEVES",
  vie: "VIERNES",
  sab: "SABADO",
  dom: "DOMINGO",
};

const pickDiasActivos = (dias) =>
  Object.entries(dias)
    .filter(([, v]) => !!v)
    .map(([k]) => DIAS_LABELS[k]);

const pickDiasInactivos = (dias) =>
  Object.entries(dias)
    .filter(([, v]) => !v)
    .map(([k]) => DIAS_LABELS[k]);

const isNumericStr = (v) => typeof v === "string" && /^\d+$/.test(v.trim());
const fmtPY = (n) => Number(n).toLocaleString("es-PY");

/* ===================== PermisoTarjeta ===================== */
export default function PermisoTarjeta({
  onVolver = () => {},
  clienteNombre = "—",
  clienteEmail = null,   // 👈 NUEVO
  vehiculoMarca = "—",
  chapa = "",
  vehiculoEtiqueta = "",
  numeroTarjeta = "—",
  tarjetaId: tarjetaIdProp,
  // vienen por si hiciera falta
  clienteId = null,
  codigoTipo = null,
  // Límite de la TARJETA (TARJ_VL_LIMITE)
  valorLimite = null,
}) {
  /* ---------- estado UI ---------- */
  const [notifManual, setNotifManual] = React.useState(false);
  const [dias, setDias] = React.useState({
    lun: false,
    mar: false,
    mie: false,
    jue: false,
    vie: false,
    sab: false,
    dom: false,
  });
  const [alertaIntentoDias, setAlertaIntentoDias] = React.useState(false);

  const [montoUI, setMontoUI] = React.useState("");
  const [periodo, setPeriodo] = React.useState("Por día");
  const [creditLimits, setCreditLimits] = React.useState([]);
  const [alertaCredito, setAlertaCredito] = React.useState(false);

  const [cantCargasUI, setCantCargasUI] = React.useState("");
  const [limiteCargas, setLimiteCargas] = React.useState(null);
  const [alertaCargas, setAlertaCargas] = React.useState(false);

  /* ---------- estado de datos backend ---------- */
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [alertas, setAlertas] = React.useState([]);


  const [clienteData, setClienteData] = React.useState({
    nombre: clienteNombre || "—",
    email: clienteEmail || null,
  });

  /* ---------- Toast ---------- */
  const [toast, setToast] = React.useState({
    open: false,
    type: "success",
    message: "",
    autoClose: true,
  });

  /* ---------- Límite de la tarjeta y validación local ---------- */
  const limiteTarjeta = Number.isFinite(Number(valorLimite))
    ? Number(valorLimite)
    : null;

  const emailValido = typeof clienteData.email === "string" &&clienteData.email.trim() !== "" &&clienteData.email.includes("@");

  const requiereEmailMsg ="Correo electrónico, campo requerido para activar alertas/notificaciones.";

  // error del monto de crédito (excede el límite de la tarjeta)
  const [montoError, setMontoError] = React.useState("");
  React.useEffect(() => {
    const n = Number(montoUI);
    if (
      montoUI !== "" &&
      Number.isFinite(n) &&
      n > 0 &&
      limiteTarjeta != null &&
      n > limiteTarjeta
    ) {
      setMontoError(
        `El monto (${fmtPY(n)}) no puede superar el límite de la tarjeta (${fmtPY(
          limiteTarjeta,
        )}).`,
      );
    } else {
      setMontoError("");
    }
  }, [montoUI, limiteTarjeta]);

  React.useEffect(() => {
    if (!clienteId) return;

    let alive = true;

    const loadCliente = async () => {
      try {
        const data = await apiFetchJson(
          apiRoutes.clientes.detail(clienteId),
          { method: "GET" }
        );

        if (!alive) return;

        setClienteData({
          nombre: data?.nombre?.trim() || "—",
          email: data?.dataEmail || null,
        });
      } catch (err) {
        console.error("Error cargando cliente:", err);
      }
    };

    loadCliente();

    return () => {
      alive = false;
    };
  }, [clienteId]);

  // si ya hay un crédito agregado y supera el tope, no dejar guardar
  const creditExcedeTope = React.useMemo(() => {
    if (!Array.isArray(creditLimits) || creditLimits.length === 0) return false;
    if (limiteTarjeta == null) return false;
    const n = Number(creditLimits?.[0]?.monto || 0);
    return Number.isFinite(n) && n > limiteTarjeta;
  }, [creditLimits, limiteTarjeta]);

  /* ---------- Validación de CARGAS: mínimo 3 ---------- */
  const MIN_CARGAS = 1;
  const [cargasError, setCargasError] = React.useState("");
  const onChangeCantCargas = (e) => {
    const v = e.target.value;
    setCantCargasUI(v);
    const n = Number(v);
    if (v !== "" && (!Number.isFinite(n) || n < MIN_CARGAS)) {
      setCargasError(`Límite de cargas no puede ser menor a ${MIN_CARGAS}`);
    } else {
      setCargasError("");
    }
  };

  /* ---------- Carga inicial ---------- */
  const TARJETAS_BASE = (apiRoutes?.tarjetas?.list || "").replace(
    /\/list(?:\?.*)?$/,
    "",
  );
  const tarjetaId = tarjetaIdProp ?? 2071458; // fallback de prueba

  const loadConfig = React.useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const urlPerm = apiRoutes.tarjetaConfig.permisos.list(tarjetaId, 0, 200);
      const urlAlert = apiRoutes.tarjetaConfig.alertas.list(tarjetaId, 0, 200);
      const [permRes, alertRes] = await Promise.all([
        apiFetchJson(urlPerm, { method: "GET" }),
        apiFetchJson(urlAlert, { method: "GET" }),
      ]);

      const toItems = (res) =>
        Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.content)
          ? res.content
          : [];

      const permList = toItems(permRes);
      const alertList = toItems(alertRes);

      // CARGA MANUAL
      const cargaManual = permList.find(
        (p) =>
          normalize(p?.tipo) === TIPO_CARGA_MANUAL ||
          normalize(p?.tipo) === TIPO_CARGA_MANUAL_NOTIFICAR,
      );
      setNotifManual(normalize(cargaManual?.valor) === "SI");

      // DÍAS
      const diasEstado = {
        lun: false,
        mar: false,
        mie: false,
        jue: false,
        vie: false,
        sab: false,
        dom: false,
      };
      const permisosDia = permList.filter(
        (p) => normalize(p?.tipo) === TIPO_DIA,
      );
      permisosDia.forEach((p) => {
        const key = toKeyDia(p?.valor);
        if (key) diasEstado[key] = true;
      });
      setDias(diasEstado);

      // ALERTA DÍAS
      const hasDiaAlert = alertList.some(
        (a) => normalize(a?.tipo) === TIPO_DIA,
      );
      setAlertaIntentoDias(hasDiaAlert);

      // LÍMITE CRÉDITO (uno vigente)
      const limDia = permList.find(
        (p) => normalize(p?.tipo) === TIPO_LIM_CRED_DIA,
      );
      const limSem = permList.find(
        (p) => normalize(p?.tipo) === TIPO_LIM_CRED_SEM,
      );
      const credit = [];
      if (limDia?.valor != null && limDia?.valor !== "") {
        credit.push({ monto: Number(limDia.valor) || 0, periodo: "Por día" });
      } else if (limSem?.valor != null && limSem?.valor !== "") {
        credit.push({
          monto: Number(limSem.valor) || 0,
          periodo: "Por semana",
        });
      }
      setCreditLimits(credit);
      setAlertaCredito(yes(limDia?.notifica) || yes(limSem?.notifica));

      // CANTIDAD CARGAS DIARIA
      const cantDia = permList.find(
        (p) => normalize(p?.tipo) === TIPO_CANT_CARGAS_DIA,
      );
      setLimiteCargas(
        cantDia?.valor != null && cantDia?.valor !== ""
          ? Number(cantDia.valor) || 0
          : null,
      );
      setAlertaCargas(yes(cantDia?.notifica));

      // ALERTAS (solo lectura)
      setAlertas(alertList);
    } catch (err) {
      setErrorMsg(err?.message || "Error al cargar configuraciones");
    } finally {
      setLoading(false);
    }
  }, [TARJETAS_BASE, tarjetaId]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await loadConfig();
    })();
    return () => {
      alive = false;
    };
  }, [loadConfig]);

  /* ---------- Reglas de switches ---------- */
  const allDiasOn = React.useMemo(
    () => Object.values(dias).every(Boolean),
    [dias],
  );
  React.useEffect(() => {
    if (allDiasOn && alertaIntentoDias) setAlertaIntentoDias(false);
  }, [allDiasOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasCreditLimit = React.useMemo(
    () => Array.isArray(creditLimits) && creditLimits.length > 0,
    [creditLimits],
  );
  React.useEffect(() => {
    if (!hasCreditLimit && alertaCredito) setAlertaCredito(false);
  }, [hasCreditLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- Acciones ---------- */
  const agregarCredito = () => {
    const n = Number(montoUI);
    if (!Number.isFinite(n) || n <= 0) return;
    // si hay error por exceder el límite, simplemente no agrega (sin toast)
    if (montoError) return;

    setCreditLimits([{ monto: n, periodo }]);
    setMontoUI("");
    setPeriodo("Por día");
  };

  const agregarCargas = () => {
    const n = Number(cantCargasUI);
    if (!Number.isFinite(n) || n <= 0) return;
    // si hay error por ser menor al mínimo, no agrega (sin toast)
    if (cargasError) return;

    setLimiteCargas(Math.trunc(n));
    setCantCargasUI("");
  };

  const handleGuardar = async () => {
    try {
      setSaving(true);
      setErrorMsg("");

      // Validación final: crédito no debe exceder límite de tarjeta
      if (hasCreditLimit && limiteTarjeta != null) {
        const n = Number(creditLimits?.[0]?.monto || 0);
        if (Number.isFinite(n) && n > limiteTarjeta) {
          setSaving(false);
          setToast({
            open: true,
            type: "error",
            message: `El monto (${fmtPY(n)}) excede el límite de la tarjeta (${fmtPY(
              limiteTarjeta,
            )}).`,
            autoClose: false,
          });
          return;
        }
      }

      // 1) desactivar permisos/alertas actuales
      await apiFetchJson(
        apiRoutes.tarjetaConfig.permisos.deactivateAll(tarjetaId),
        { method: "PATCH" },
      );
      await apiFetchJson(
        apiRoutes.tarjetaConfig.alertas.deactivateAll(tarjetaId),
        { method: "PATCH" },
      );

      // 2) PERMISOS
      await apiFetchJson(apiRoutes.tarjetaConfig.permisos.create(tarjetaId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: TIPO_CARGA_MANUAL_NOTIFICAR,
          valor: notifManual ? "SI" : "NO",
          notifica: notifManual ? "SI" : "NO",
        }),
      });

      const diasActivos = pickDiasActivos(dias);
      for (const d of diasActivos) {
        await apiFetchJson(apiRoutes.tarjetaConfig.permisos.create(tarjetaId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tipo: TIPO_DIA, valor: d, notifica: "NO" }),
        });
      }

      let tipoCredito = null;
      let valorCredito = null;
      if (hasCreditLimit) {
        const c = creditLimits[0];
        const monto = String(Math.trunc(Number(c?.monto || 0) || 0));
        const periodoUp = normalize(c?.periodo || "");
        tipoCredito = periodoUp.includes("SEMANA")
          ? TIPO_LIM_CRED_SEM
          : TIPO_LIM_CRED_DIA;
        valorCredito = monto;

        await apiFetchJson(apiRoutes.tarjetaConfig.permisos.create(tarjetaId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tipo: tipoCredito,
            valor: valorCredito,
            notifica: alertaCredito ? "SI" : "NO",
          }),
        });
      }

      let valorCargas = null;
      if (
        typeof limiteCargas === "number" &&
        Number.isFinite(limiteCargas) &&
        limiteCargas > 0
      ) {
        valorCargas = String(Math.trunc(limiteCargas));
        await apiFetchJson(apiRoutes.tarjetaConfig.permisos.create(tarjetaId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tipo: TIPO_CANT_CARGAS_DIA,
            valor: valorCargas,
            notifica: alertaCargas ? "SI" : "NO",
          }),
        });
      }

      // 3) ALERTAS
      if (notifManual) {
        await apiFetchJson(apiRoutes.tarjetaConfig.alertas.create(tarjetaId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tipo: TIPO_CARGA_MANUAL_NOTIFICAR,
            valor: "SI",
          }),
        });
      }

      const allOn = Object.values(dias).every(Boolean);
      if (alertaIntentoDias && !allOn) {
        const diasOff = pickDiasInactivos(dias);
        for (const d of diasOff) {
          await apiFetchJson(apiRoutes.tarjetaConfig.alertas.create(tarjetaId), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tipo: TIPO_DIA, valor: d }),
          });
        }
      }

      if (
        hasCreditLimit &&
        alertaCredito &&
        tipoCredito &&
        valorCredito != null
      ) {
        await apiFetchJson(apiRoutes.tarjetaConfig.alertas.create(tarjetaId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tipo: tipoCredito, valor: valorCredito }),
        });
      }

      if (valorCargas != null && alertaCargas) {
        await apiFetchJson(apiRoutes.tarjetaConfig.alertas.create(tarjetaId), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tipo: TIPO_CANT_CARGAS_DIA,
            valor: valorCargas,
          }),
        });
      }

      // 4) rehidratar
      await loadConfig();

      setToast({
        open: true,
        type: "success",
        message: "La configuración se guardó correctamente.",
        autoClose: true,
      });
    } catch (err) {
      setErrorMsg(err?.message || "Error al guardar configuraciones");

      setToast({
        open: true,
        type: "error",
        message:
          err?.message || "Ocurrió un error al guardar. Intente nuevamente.",
        autoClose: false,
      });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Presentación ---------- */
  const patenteMostrar =
    (chapa && chapa !== "—"
      ? chapa
      : vehiculoEtiqueta && vehiculoEtiqueta !== "—"
      ? vehiculoEtiqueta
      : "") || "—";
  const vehiculoMostrar =
    vehiculoMarca && vehiculoMarca !== "—" ? vehiculoMarca : "—";

  return (
    <div className="page">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button type="button" className="btn-fil" onClick={onVolver}>
          <FaArrowLeft className="inline mr-2" /> Volver
        </button>

        <h2 className="page-title m-0 flex-1">Configuración permisos de tarjetas</h2>

        <button
          type="button"
          className="btn-fil"
          onClick={handleGuardar}
          disabled={saving || loading || creditExcedeTope}
          title={
            creditExcedeTope
              ? "El monto asignado excede el límite de la tarjeta"
              : "Guardar configuración actual"
          }
        >
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>

      {loading && (
        <div className="mb-3 text-sm text-gray-600">Cargando configuraciones…</div>
      )}
      {!!errorMsg && (
        <div className="mb-3 text-sm text-red-600">Error: {errorMsg}</div>
      )}

      {/* Datos superiores */}
      <div className="mb-4 rounded-md border border-gray-200 bg-white px-4 py-3 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <div className="value">Cliente :</div>
            <div className="font-medium text-gray-800">{clienteData.nombre}</div>

            <div className="value mt-2">Correo electrónico:</div>
            <div className={`font-medium ${emailValido ? "text-gray-800" : "text-red-600"}`}>
              {emailValido  ? clienteData.email : "Campo requerido para activar alertas/notificacones"}
            </div>


            <div className="value mt-2">Patente del vehículo:</div>
            <div className="font-medium text-gray-800">{patenteMostrar}</div>
          </div>

          <div className="md:text-right">
            <div className="value">Vehículo :</div>
            <div className="font-medium text-gray-800">{vehiculoMostrar}</div>

            <div className="value mt-2">Nro tarjeta:</div>
            <div className="font-medium text-gray-800">{numeroTarjeta}</div>
          </div>
        </div>
      </div>

      {/* 1) Notificación de cargas manuales */}
      <Section title="Notificación de cargas manuales" defaultOpen>
        <Switch
          checked={notifManual}
          onChange={(v) => {
            if (!emailValido) return;
            setNotifManual(v);
          }}
          label="Notificación de carga manual"
          disabled={!emailValido}
        />
      </Section>

      {/* 2) Permisos por día */}
      <Section title="Permisos por día" defaultOpen>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Switch checked={dias.lun} onChange={(v) => setDias({ ...dias, lun: v })} label="Lunes" />
          <Switch checked={dias.mar} onChange={(v) => setDias({ ...dias, mar: v })} label="Martes" />
          <Switch checked={dias.mie} onChange={(v) => setDias({ ...dias, mie: v })} label="Miércoles" />
          <Switch checked={dias.jue} onChange={(v) => setDias({ ...dias, jue: v })} label="Jueves" />
          <Switch checked={dias.vie} onChange={(v) => setDias({ ...dias, vie: v })} label="Viernes" />
          <Switch checked={dias.sab} onChange={(v) => setDias({ ...dias, sab: v })} label="Sábado" />
          <Switch checked={dias.dom} onChange={(v) => setDias({ ...dias, dom: v })} label="Domingo" />
        </div>

        <div className="mt-4">
          <Switch
            checked={alertaIntentoDias}
            onChange={(v) => {
              if (!emailValido) return;
              setAlertaIntentoDias(v);
            }}
            label="Alerta de intento restringido"
            disabled={allDiasOn || !emailValido}
          />
        </div>
      </Section>

      {/* 3) Permiso de crédito */}
      <Section title="Permiso de crédito" defaultOpen>
        {/* Límite visible */}
        {valorLimite != null && (
          <div className="mb-3 text-sm">
            <span className="value mr-2">Limite de la tarjeta:</span>
            <b>{fmtPY(valorLimite)}</b>
          </div>
        )}

        <p className="text-xs text-gray-600 mb-2">
          <em>Seleccionar UNA SOLA OPCIÓN. Límite por día o límite por semana.</em>
        </p>

        {/* Mensaje en una sola línea */}
        {montoError && <div className="text-xs text-red-600 mb-2">{montoError}</div>}

        <div className="flex flex-wrap items-center gap-2">
          <div className="w-36">
            <input
              type="number"
              className={`w-full border rounded px-2 py-2 ${
                montoError ? "border-red-500 focus:outline-none focus:ring-2 focus:ring-red-300" : ""
              }`}
              placeholder="Monto"
              value={montoUI}
              onChange={(e) => setMontoUI(e.target.value)}
              aria-invalid={!!montoError}
            />
          </div>

          <select className="border rounded px-2 py-2" value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
            <option>Por día</option>
            <option>Por semana</option>
          </select>

          {/* Botón apagado cuando hay error */}
          <button
            type="button"
            className={montoError ? "btn-fil-disabled" : "btn-fil"}
            onClick={agregarCredito}
            disabled={!!montoError}
            title={montoError ? "Corrige el monto para continuar" : ""}
          >
            AGREGAR
          </button>

          <div className="ml-auto">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {creditLimits.length === 0 ? (
                <div>Sin límites de créditos asignados.</div>
              ) : (
                <>
                  <div className="mb-1">Límites asignados:</div>
                  <div className="flex flex-wrap gap-2">
                    {creditLimits.map((c, i) => (
                      <Pill key={i} onRemove={() => setCreditLimits([])}>
                        {`${Number(c.monto || 0).toLocaleString("es-PY")} · ${c.periodo}`}
                      </Pill>
                    ))}
                  </div>
                </>
              )}
            </div>

            {creditExcedeTope && (
              <p className="mt-2 text-xs text-red-600">El monto asignado excede el límite de la tarjeta.</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <Switch
            checked={alertaCredito}
            onChange={(v) => {
              if (!emailValido) return;
              setAlertaCredito(v);
            }}
            label="Alerta de intento restringido"
            disabled={!hasCreditLimit || !emailValido}
          />
        </div>
      </Section>

      {/* 4) Cantidad máxima de cargas diarias */}
      <Section title="Cantidad máxima de cargas diarias" defaultOpen>
        {/* mensaje bajo el título, una sola línea */}
        {cargasError && (
          <div className="text-sm font-semibold text-red-600 mb-2">{cargasError}</div>
        )}

        <div className="flex flex-col gap-4 py-2">
          <div className="flex items-center justify-center gap-2">
            <input
              type="number"
              className={`w-40 border rounded px-2 py-2 text-center ${
                cargasError ? "border-red-500 focus:outline-none focus:ring-2 focus:ring-red-300" : ""
              }`}
              placeholder="Cantidad"
              value={cantCargasUI}
              onChange={onChangeCantCargas}
              aria-invalid={!!cargasError}
            />
            <button
              type="button"
              className={cargasError ? "btn-fil-disabled" : "btn-fil"}
              onClick={agregarCargas}
              disabled={!!cargasError || !String(cantCargasUI).trim()}
              title={cargasError ? "Corrige la cantidad para continuar" : ""}
            >
              AGREGAR
            </button>
          </div>

          {limiteCargas != null && (
            <div className="text-sm text-gray-700 text-center">
              Límite aplicado: <b>{limiteCargas}</b> carga(s) por día.
            </div>
          )}

          <div className="flex items-center justify-center">
            <Switch
              checked={alertaCargas}
              onChange={(v) => {
                if (!emailValido) return;
                setAlertaCargas(v);
              }}
              label="Alerta de intento restringido"
              disabled={!emailValido}
            />
          </div>
        </div>
      </Section>

      {/* 5) Alertas (solo lectura) */}
      <Section title="Alertas configuradas" defaultOpen>
        {alertas.length === 0 ? (
          <div className="text-sm text-gray-600">Sin alertas configuradas.</div>
        ) : (
          <ul className="list-disc ml-5 text-sm">
            {alertas.map((a) => {
              const valor = a?.valor;
              const mostrarValor = isNumericStr(valor) ? fmtPY(valor) : valor ?? "—";
              return (
                <li key={a.id}>
                  <span className="font-medium">{a.tipo}</span>
                  {": "}
                  <span>{mostrarValor}</span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Toast */}
      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        autoClose={toast.autoClose}
        onClose={() => {
          setToast((t) => ({ ...t, open: false }));
          if (toast.type === "success" && toast.autoClose) onVolver();
        }}
      />
    </div>
  );
}